import React, { useEffect, useState } from "react";
import axios from "axios";
import "./StudentICard.css";
import AdminHeader from "../common/AdminHeader";
import { baseUrl } from "../../Utility/constants";
import { getSchoolId } from "../../Utility/commonFunctions";
import { toast, ToastContainer } from "react-toastify";

const StudentICard = () => {

  // =========================================================
  // BASIC STATE
  // =========================================================

  const [schoolId, setSchoolId] = useState("");
  const [role, setRole] = useState("");
  const [userId, setUserId] = useState("");

  const [reportData, setReportData] = useState([]);

  // =========================================================
  // SCHOOL INFORMATION
  // =========================================================

  const [schoolInfo, setSchoolInfo] = useState(null);

  // =========================================================
  // FILTER DATA
  // =========================================================

  const [standards, setStandards] = useState([]);
  const [sections, setSections] = useState([]);
  const [streams, setStreams] = useState([]);

  const [selectedStandardId, setSelectedStandardId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");
  const [selectedStreamId, setSelectedStreamId] = useState("");

  const [standardValue, setStandardValue] = useState("");

  // =========================================================
  // ADMIN - ID CARD SELECTION
  // =========================================================

  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [previewStudentId, setPreviewStudentId] = useState(null);

  // =========================================================
  // API URLs
  // =========================================================

  const standardApiUrl =
    `${baseUrl}/api/StandardMaster/GetStandardBySchool`;

  const sectionApiUrl =
    `${baseUrl}/api/SectionMaster/GetSectionByStandardID`;

  const streamApiUrl =
    `${baseUrl}/api/StreamMaster`;

  // =========================================================
  // LOAD SCHOOL INFORMATION
  // =========================================================

  useEffect(() => {
    if (schoolId) {
      fetchSchoolInfo();
    }
  }, [schoolId]);

  const fetchSchoolInfo = async () => {
    try {

      const id = await getSchoolId();

      if (!id) {
        console.error("School ID not found.");
        return;
      }

      const response = await axios.get(
        `${baseUrl}/api/SchoolGeneralInfo/GetSchoolInfo?schoolId=${id}`
      );

      setSchoolInfo(response.data);

    } catch (error) {

      console.error(
        "Error loading school information:",
        error
      );

    }
  };

  // =========================================================
  // LOAD USER DETAILS
  // =========================================================

  useEffect(() => {

    const loadUserDetails = async () => {

      const id = await getSchoolId();

      if (id) {
        setSchoolId(id);
      }

      setRole(
        localStorage.getItem("Role") || ""
      );

      setUserId(
        localStorage.getItem("userID") || ""
      );
    };
    loadUserDetails();
  }, []);

  // =========================================================
  // LOAD STANDARDS
  // =========================================================

  useEffect(() => {

    if (schoolId && role === "ADMIN") {

      axios
        .get(`${standardApiUrl}?id=${schoolId}`)
        .then((res) => {

          setStandards(res.data || []);

        })
        .catch((error) => {

          console.error(
            "Error fetching standards:",
            error
          );

          toast.error(
            "Failed to load standards."
          );

        });
    }

  }, [schoolId, role]);

  // =========================================================
  // LOAD STREAMS
  // =========================================================

  useEffect(() => {

    if (role === "ADMIN") {

      axios
        .get(streamApiUrl)
        .then((res) => {

          setStreams(res.data || []);

        })
        .catch((error) => {

          console.error(
            "Error fetching streams:",
            error
          );

        });
    }

  }, [role]);

  // =========================================================
  // LOAD SECTIONS
  // =========================================================

  useEffect(() => {

    if (
      role === "ADMIN" &&
      selectedStandardId
    ) {

      axios
        .get(
          `${sectionApiUrl}?id=${selectedStandardId}`
        )
        .then((res) => {

          setSections(res.data || []);

        })
        .catch((error) => {

          console.error(
            "Error fetching sections:",
            error
          );

          setSections([]);

        });

    } else {

      setSections([]);
      setSelectedSectionId("");

    }

  }, [selectedStandardId, role]);

  // =========================================================
  // FETCH STUDENTS
  // =========================================================

  const fetchData = async () => {

    try {

      let url = "";

      // -----------------------------------------------------
      // ADMIN
      // -----------------------------------------------------

      if (role === "ADMIN") {

        url =
          `${baseUrl}/api/PersonalDetails/StudentIDCards` +
          `?AdminId=${userId}` +
          `&StandardId=${selectedStandardId || ""}` +
          `&SectionId=${selectedSectionId || ""}` +
          `&StreamId=${selectedStreamId || ""}`;

      }

      // -----------------------------------------------------
      // TEACHER
      // -----------------------------------------------------

      else if (role === "TEACHER") {

        url =
          `${baseUrl}/api/PersonalDetails/StudentIDCards` +
          `?TeacherId=${userId}`;

      }

      // -----------------------------------------------------
      // INVALID ROLE
      // -----------------------------------------------------

      else {

        toast.warning(
          "Invalid role."
        );

        return;
      }

      const response = await axios.get(url);

      const data = response.data || [];

      setReportData(data);

      // Reset selection after fetching
      setSelectedStudentIds([]);
      setSelectAll(false);

      if (data.length === 0) {

        toast.warning(
          "No students found."
        );

      }

    } catch (error) {

      console.error(
        "Failed to fetch student ID card data:",
        error
      );

      toast.error(
        "Failed to fetch student data."
      );

    }
  };

  // =========================================================
  // AUTO FETCH FOR TEACHER
  // =========================================================

  useEffect(() => {

    if (
      role === "TEACHER" &&
      userId
    ) {

      fetchData();

    }

  }, [role, userId]);

  // =========================================================
  // STANDARD CHANGE
  // =========================================================

  const handleChange = (e) => {

    const value = e.target.value;

    setSelectedStandardId(value);

    setStandardValue(
      e.target.options[
        e.target.selectedIndex
      ]?.text || ""
    );

    setSelectedSectionId("");
    setSelectedStreamId("");

  };

  // =========================================================
  // ADMIN - INDIVIDUAL STUDENT SELECTION
  // =========================================================

  const handleStudentSelection = (studentId) => {

    setSelectedStudentIds((prev) => {

      if (prev.includes(studentId)) {

        return prev.filter(
          (id) => id !== studentId
        );

      }

      return [
        ...prev,
        studentId
      ];

    });

  };

  // =========================================================
  // ADMIN - SELECT ALL
  // =========================================================

  const handleSelectAll = () => {

    if (selectAll) {

      setSelectedStudentIds([]);
      setSelectAll(false);

      return;
    }

    const allIds = reportData
      .map(
        (student) => student.studentId
      )
      .filter(
        (id) =>
          id !== null &&
          id !== undefined
      );

    setSelectedStudentIds(allIds);
    setSelectAll(true);

  };

  // =========================================================
  // KEEP SELECT ALL IN SYNC
  // =========================================================

  useEffect(() => {

    if (reportData.length === 0) {

      setSelectAll(false);

      return;
    }

    const allIds = reportData
      .map(
        (student) => student.studentId
      )
      .filter(
        (id) =>
          id !== null &&
          id !== undefined
      );

    const allSelected =
      allIds.length > 0 &&
      allIds.every(
        (id) =>
          selectedStudentIds.includes(id)
      );

    setSelectAll(allSelected);

  }, [
    selectedStudentIds,
    reportData
  ]);

  // =========================================================
  // PRINT SELECTED ID CARDS
  // =========================================================

  const handlePrintSelected = () => {

    if (
      selectedStudentIds.length === 0
    ) {

      toast.warning(
        "Please select at least one student."
      );

      return;
    }

    const selectedStudents =
      reportData.filter(
        (student) =>
          selectedStudentIds.includes(
            student.studentId
          )
      );

    if (selectedStudents.length === 0) {

      toast.error(
        "Selected students could not be found."
      );

      return;
    }

    console.log(
      "Printing students:",
      selectedStudents
    );

    // Browser print.
    // CSS @media print will hide the normal page
    // and display only the ID card print area.
    window.print();

  };

  // =========================================================
  // TEACHER - VIEW ID CARD
  // =========================================================

  const handleViewIdCard = (student) => {
    setPreviewStudentId(student.studentId);
  };

  // =========================================================
  // FORMAT DATE
  // =========================================================

  const formatDate = (date) => {

    if (!date) {
      return "-";
    }

    const parsedDate =
      new Date(date);

    if (
      isNaN(
        parsedDate.getTime()
      )
    ) {

      return date;

    }

    return parsedDate.toLocaleDateString(
      "en-IN"
    );

  };

  // =========================================================
  // GET FULL NAME
  // =========================================================

  const getFullName = (student) => {

    return [
      student.firstName,
      student.middleName,
      student.lastName
    ]
      .filter(Boolean)
      .join(" ");

  };

  // =========================================================
  // SCHOOL LOGO
  // =========================================================

  const schoolLogo =
    schoolInfo?.logo || "";

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>

      {/* =====================================================
          NORMAL APPLICATION
      ===================================================== */}

      <div className="screen-content">

        <AdminHeader />

        <ToastContainer />

        <div className="mt-5 mb-5 ms-5 me-5">

          {/* =================================================
              HEADER
          ================================================= */}

          <div
            className="
              d-flex
              align-items-center
              justify-content-center
              gap-3
              mb-4
              flex-wrap
            "
          >

            <h2 className="mb-0 me-3 text-nowrap">
              Student ID Cards
            </h2>

            {/* =================================================
                ADMIN FILTERS
            ================================================= */}

            {role === "ADMIN" && (
              <>

                {/* STANDARD */}

                <select
                  className="form-control"
                  value={selectedStandardId}
                  onChange={handleChange}
                  style={{
                    width: "180px",
                    minWidth: "180px"
                  }}
                >

                  <option value="">
                    Select Standard
                  </option>

                  {standards.map(
                    (std) => (
                      <option
                        key={std.id}
                        value={std.id}
                      >
                        {std.standardName}
                      </option>
                    )
                  )}

                </select>

                {/* SECTION */}

                <select
                  className="form-control"
                  value={selectedSectionId}
                  onChange={(e) =>
                    setSelectedSectionId(
                      e.target.value
                    )
                  }
                  disabled={
                    !sections.length
                  }
                  style={{
                    width: "180px",
                    minWidth: "180px"
                  }}
                >

                  <option value="">
                    Select Section
                  </option>

                  {sections.map(
                    (sec) => (
                      <option
                        key={sec.id}
                        value={sec.id}
                      >
                        {sec.name}
                      </option>
                    )
                  )}

                </select>

                {/* STREAM */}

                <select
                  className="form-control"
                  value={selectedStreamId}
                  onChange={(e) =>
                    setSelectedStreamId(
                      e.target.value
                    )
                  }
                  disabled={
                    standardValue &&
                    Number(
                      standardValue
                    ) <= 10
                  }
                  style={{
                    width: "180px",
                    minWidth: "180px"
                  }}
                >

                  <option value="">
                    Select Stream
                  </option>

                  {streams.map(
                    (stream) => (
                      <option
                        key={stream.id}
                        value={stream.id}
                      >
                        {stream.name}
                      </option>
                    )
                  )}

                </select>

                {/* SEARCH */}

                <button
                  type="button"
                  className="
                    btn
                    btn-primary
                    text-nowrap
                  "
                  onClick={fetchData}
                >
                  Search
                </button>

              </>
            )}

            {/* =================================================
                ADMIN PRINT BUTTON
            ================================================= */}

            {role === "ADMIN" &&
              reportData.length > 0 && (

                <button
                  type="button"
                  className="
                    btn
                    btn-success
                    text-nowrap
                  "
                  onClick={
                    handlePrintSelected
                  }
                  disabled={
                    selectedStudentIds.length === 0
                  }
                >

                  Print Selected ID Cards

                  {selectedStudentIds.length > 0 &&
                    ` (${selectedStudentIds.length})`
                  }

                </button>

              )}

          </div>

          {/* =====================================================
              STUDENT TABLE
          ===================================================== */}

          {reportData.length > 0 && (

            <div className="table-responsive">

              <table
                className="
                  table
                  table-bordered
                  table-striped
                  table-hover
                  mb-0
                "
                style={{
                  width: "100%",
                  tableLayout: "fixed"
                }}
              >

                {/* =================================================
                    TABLE HEADER
                ================================================= */}

                <thead className="table-dark">

                  <tr>

                    {/* SELECT ALL */}

                    {role === "ADMIN" && (

                      <th
                        style={{
                          width: "70px",
                          textAlign: "center",
                          verticalAlign: "middle"
                        }}
                      >

                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectAll}
                          onChange={
                            handleSelectAll
                          }
                        />

                      </th>

                    )}

                    {/* STUDENT NAME */}

                    <th
                      style={{
                        textAlign: "center",
                        width: "120px",
                      }}
                    >
                      Student Name
                    </th>

                    {/* CLASS */}

                    <th
                      style={{
                        width: "120px",
                        textAlign: "center"
                      }}
                    >
                      Class
                    </th>

                    {/* SECTION */}

                    <th
                      style={{
                        width: "120px",
                        textAlign: "center"
                      }}
                    >
                      Section
                    </th>

                    {/* ROLL NUMBER */}

                    <th
                      style={{
                        width: "120px",
                        textAlign: "center"
                      }}
                    >
                      Roll No
                    </th>
                    <th
                      style={{
                        width: "120px",
                        textAlign: "center"
                      }}
                    >
                      GR. No.
                    </th>

                    {/* CONTACT */}

                    <th
                      style={{
                        width: "160px",
                        textAlign: "center"
                      }}
                    >
                      Contact No
                    </th>

                    {/* STUDENT ACTION */}

                    <th
                      style={{
                        width: "150px",
                        textAlign: "center"
                      }}
                    >
                      Action
                    </th>

                  </tr>

                </thead>

                {/* =================================================
                    TABLE BODY
                ================================================= */}

                <tbody>

                  {reportData.map(
                    (student, index) => (

                      <tr
                        key={
                          student.studentId ||
                          index
                        }
                      >

                        {/* ADMIN CHECKBOX */}

                        {role === "ADMIN" && (

                          <td
                            style={{
                              textAlign: "center",

                            }}
                          >

                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={selectedStudentIds.includes(
                                student.studentId
                              )}
                              onChange={() =>
                                handleStudentSelection(
                                  student.studentId
                                )
                              }
                            />

                          </td>

                        )}

                        {/* STUDENT NAME */}

                        <td
                          style={{
                            textAlign: "left",
                            verticalAlign: "middle",
                            fontWeight: "500"
                          }}
                        >
                          {getFullName(student)}
                        </td>

                        {/* CLASS */}

                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle"
                          }}
                        >
                          {student.standardName || "-"}
                        </td>

                        {/* SECTION */}

                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle"
                          }}
                        >
                          {student.sectionName || "-"}
                        </td>

                        {/* ROLL NO */}

                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle"
                          }}
                        >
                          {student.rollNo || "-"}
                        </td>

                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle"
                          }}
                        >
                          {student.generalRegisterNo || "-"}
                        </td>

                        {/* CONTACT */}

                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle"
                          }}
                        >
                          {student.studentContactNumber || "-"}
                        </td>

                        {/* STUDENT ACTION */}

                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle"
                          }}
                        >
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() =>
                              handleViewIdCard(student)
                            }
                          >
                            View ID Card
                          </button>
                        </td>

                      </tr>

                    )
                  )}

                </tbody>

              </table>

            </div>

          )}

        </div>

      </div>


      {/* =========================================================
    PRINT ONLY ID CARDS
    ========================================================= */}

      <div
        className={`id-card-print-area${previewStudentId !== null ? " id-card-preview-overlay" : ""}`}
      >

        {(previewStudentId !== null
          ? reportData.filter(
            (student) => student.studentId === previewStudentId
          )
          : reportData.filter((student) =>
            selectedStudentIds.includes(student.studentId)
          )
        )
          .map((student) => (

            <div
              className="id-card"
              key={student.studentId}
            >

              {previewStudentId !== null && (
                <button
                  type="button"
                  className="id-card-preview-close"
                  onClick={() => setPreviewStudentId(null)}
                  aria-label="Close ID card preview"
                  title="Close preview"
                >
                  &times;
                </button>
              )}

              {/* =================================================
            HEADER
        ================================================= */}

              <div className="id-card-header">

                {/* SCHOOL LOGO */}

                <div className="school-logo">

                  {schoolLogo && (
                    <img
                      src={schoolLogo}
                      alt="School Logo"
                    />
                  )}

                </div>


                {/* SCHOOL INFORMATION */}

                <div className="school-details">

                  <div className="organization-name">
                    {schoolInfo?.organizationName || ""}
                  </div>

                  <div className="school-name">
                    {schoolInfo?.schoolOrCollegeName || ""}
                  </div>

                  <div className="school-address">
                    {schoolInfo?.address || ""}
                  </div>

                  <div className="school-contact">
                    Ph:{" "}
                    {schoolInfo?.schoolOrCollegeContactNo || ""}
                  </div>

                </div>

              </div>


              {/* =================================================
            SESSION
        ================================================= */}

              <div className="card-session">
                <b> Academic Year {student.sessionName}</b>
              </div>


              {/* =================================================
            MAIN CONTENT
        ================================================= */}

              <div className="id-card-body">

                {/* ---------------------------------------------
              PHOTO
          --------------------------------------------- */}

                <div className="student-photo">

                  {student.photo ? (

                    <img
                      src={student.photo}
                      alt={getFullName(student)}
                    />

                  ) : (

                    <div className="no-photo">
                      PHOTO
                    </div>

                  )}

                </div>


                {/* ---------------------------------------------
              STUDENT INFORMATION
          --------------------------------------------- */}

                <div className="student-details">

                  {/* NAME */}

                  <div className="detail-group mid-group">

                    <span className="detail-label">
                      Roll No / GR No
                    </span>

                    <span className="detail-colon">
                      :
                    </span>

                    <span className="detail-value">
                      {student.rollNo || "-"}
                    </span>
                    <span className="detail-colon">
                      /
                    </span>
                    <span className="detail-value">
                      {student.generalRegisterNo || "-"}
                    </span>

                  </div>

                  <div className="detail-row name-row">

                    <span className="detail-label">
                      Student Name
                    </span>
                    <span className="detail-colon">
                      :
                    </span>
                    <span className="detail-value student-name">
                      {getFullName(student)}
                    </span>

                  </div>


                  {/* CLASS + M.ID */}

                  <div className="detail-row split-row">

                    <div className="detail-group">

                      <span className="detail-label">
                        Class/Stream/Division
                      </span>
                      <span className="detail-colon">
                        :
                      </span>
                      <span className="detail-value">
                        {student.standardName || "-"}
                        {student.streamName
                          ? ` ${student.streamName}`
                          : ""}
                        {student.sectionName
                          ? ` ${student.sectionName}`
                          : ""}
                      </span>
                    </div>
                  </div>

                  <div className="detail-row split-row">

                    <div className="detail-group">

                      <span className="detail-label">
                        Gender / Blood Group
                      </span>

                      <span className="detail-colon">
                        :
                      </span>

                      <span className="detail-value">
                        {student.genderName || "-"} {" "}/{" "}
                        {student.bloodGroupName || "-"}
                      </span>

                    </div>


                  </div>


                  {/* DOB */}

                  <div className="detail-row split-row">

                    <div className="detail-group">

                      <span className="detail-label">
                        Date of Birth
                      </span>

                      <span className="detail-colon">
                        :
                      </span>

                      <span className="detail-value">
                        {formatDate(
                          student.dateOfBirth
                        )}
                      </span>

                    </div>


                  </div>

                  {/* Blood Group */}

                  {/* <div className="detail-row split-row">
                    <div className="detail-group">

                      <span className="detail-label">
                        Blood Group
                      </span>

                      <span className="detail-colon">
                        :
                      </span>

                      <span className="detail-value">
                        {student.bloodGroupName || "-"}
                      </span>

                    </div>
                  </div> */}

                   {/* CONTACT */}

                  <div className="detail-row split-row">
                    <div className="detail-group">
                      <span className="detail-label">
                        Contact Number
                      </span>
                      <span className="detail-colon">
                        :
                      </span>
                      <span className="detail-value">
                        {student.studentContactNumber || "-"}
                      </span>
                    </div>
                  </div>

                  {/* ADDRESS */}

                  <div className="detail-row split-row">
                    <div className="detail-group">
                      <span className="detail-label">
                        Address
                      </span>
                      <span className="detail-colon">
                        :
                      </span>
                      <span className="detail-value">
                        {student.address || "-"}
                      </span>
                    </div>
                  </div>


                </div>
              </div>

              {/* =================================================
            FOOTER
            ================================================= */}

              <div className="id-card-footer">

                {/* SIGNATURE - BOTTOM LEFT */}

                {/* SIGNATURE + STAMP - BOTTOM LEFT */}

                <div className="signature-area">

                  {/* PRINCIPAL SIGNATURE - TOP */}
                  {schoolInfo?.principalSignture && (
                    <div className="signature-image-container">
                      <img
                        src={schoolInfo.principalSignture}
                        alt="Principal Signature"
                        className="signature-image"
                      />
                    </div>
                  )}

                  {/* SCHOOL STAMP - BELOW SIGNATURE */}
                  {schoolInfo?.schoolCollegeStamp && (
                    <div className="stamp-image-container">
                      <img
                        src={schoolInfo.schoolCollegeStamp}
                        alt="School Stamp"
                        className="stamp-image"
                      />
                    </div>
                  )}

                </div>

                {/* BARCODE - BOTTOM RIGHT 
                <div className="barcode-area">

                  <div className="barcode">
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>

                </div>
*/}
              </div>

            </div>

          ))}

      </div>

    </>
  );
};

export default StudentICard;
